import type { MetaReducer } from '@ngrx/store';
import { NGRX_STORAGE_KEYS } from '../../../../core/store/ngrx-persisted-state.keys';
import { createStorageSyncMetaReducer } from '../../../../core/store/ngrx-storage-sync.meta-reducer';
import { StudentsListFeatureState } from './students-list.models';
import { studentsListFeature, initialStudentsListState } from './students-list.reducer';
import { getPaginationPage } from './students-list.cache';

function isUnsetIdList(value: string[] | null | undefined): boolean {
  return value == null || value.length === 0;
}

function toIdList(value: unknown): string[] | null {
  if (Array.isArray(value)) {
    const ids = value.map((id) => String(id)).filter((id) => id.trim() !== '');
    return ids.length > 0 ? ids : null;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    return [value.trim()];
  }

  return null;
}

function shouldClearFromStorage(state: StudentsListFeatureState): boolean {
  const filters = state.filters;
  const filtersDefault =
    (filters.studentId === null || filters.studentId === '') &&
    (filters.studentLabel === null || filters.studentLabel === '') &&
    isUnsetIdList(filters.careerCoachId) &&
    isUnsetIdList(filters.programId) &&
    filters.upcomingInterview === null &&
    isUnsetIdList(filters.technicalMentorId) &&
    isUnsetIdList(filters.salesTeamUserId) &&
    isUnsetIdList(filters.lifeCycleStatusId) &&
    isUnsetIdList(filters.cseAssignedId) &&
    isUnsetIdList(filters.activeExitedStatusId);
  const paginationDefault =
    state.pagination.firstRowOffset === 0 &&
    state.pagination.pageSize === initialStudentsListState.pagination.pageSize &&
    getPaginationPage(state.pagination) === 1 &&
    state.pagination.totalRecords === 0;
  const sortDefault =
    state.sort.sortField === '' &&
    state.sort.sortOrder === 1 &&
    state.sort.previousSortField === '' &&
    state.sort.previousSortOrder === 0;
  const noGridData = state.gridData === null;
  return filtersDefault && paginationDefault && sortDefault && noGridData;
}

function parseState(raw: unknown): StudentsListFeatureState | null {
  if (!raw || typeof raw !== 'object') return null;
  const partial = raw as Partial<StudentsListFeatureState>;
  if (!partial.filters || !partial.pagination || !partial.sort) return null;

  const paginationPartial = partial.pagination as StudentsListFeatureState['pagination'];
  const rawGridData = partial.gridData ?? null;
  const legacyTotalRecords =
    rawGridData && typeof rawGridData === 'object' && 'totalRecords' in rawGridData
      ? Number((rawGridData as { totalRecords?: number }).totalRecords) || 0
      : null;
  const normalizedGridData =
    rawGridData && typeof rawGridData === 'object' && Array.isArray(rawGridData.students)
      ? { students: rawGridData.students }
      : null;

  const parsedPagination: StudentsListFeatureState['pagination'] = {
    ...initialStudentsListState.pagination,
    ...paginationPartial,
    page: paginationPartial.page ?? getPaginationPage({
      ...initialStudentsListState.pagination,
      ...paginationPartial
    }),
    totalRecords: paginationPartial.totalRecords ?? legacyTotalRecords ?? 0
  };

  return {
    filters: {
      ...initialStudentsListState.filters,
      ...partial.filters,
      studentId:
        partial.filters.studentId ??
        (partial.filters as { fullName?: string | null }).fullName ??
        null,
      studentLabel:
        partial.filters.studentLabel ??
        (partial.filters as { fullNameLabel?: string | null }).fullNameLabel ??
        null,
      careerCoachId: toIdList(partial.filters.careerCoachId),
      programId: toIdList(partial.filters.programId),
      technicalMentorId: toIdList(partial.filters.technicalMentorId),
      salesTeamUserId: toIdList(partial.filters.salesTeamUserId),
      lifeCycleStatusId: toIdList(
        partial.filters.lifeCycleStatusId ??
          (partial.filters as { companyLevelStatusId?: string | null }).companyLevelStatusId
      ),
      cseAssignedId: toIdList(partial.filters.cseAssignedId),
      activeExitedStatusId: toIdList(partial.filters.activeExitedStatusId)
    },
    pagination: parsedPagination,
    sort: partial.sort,
    gridData: normalizedGridData
  };
}

export const studentsListMetaReducers: MetaReducer[] = [
  createStorageSyncMetaReducer<StudentsListFeatureState>({
    featureKey: studentsListFeature.name,
    storageKey: NGRX_STORAGE_KEYS.studentsList,
    shouldClearFromStorage,
    serialize: (state) => state,
    deserialize: parseState
  })
];
